import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import KindergartenCard from '../KindergartenCard/KindergartenCard';
import SearchFilterSection from '../SearchFilterSection/SearchFilterSection';
import ResultsSection from '../ResultsSection/ResultsSection';
import MapPlaceholder from '../../../../../components/MapPlaceholder/MapPlaceholder';
import KindergartensTable from '../KindergartensTable/KindergartensTable';

import './KindergartensPage.css';

import { getKindergartensList } from '../../actions';
import { getFilterObj } from '../../data/filters';

const SELECTED_KG_PERM = 'cabinetkgselected';
const MAP_LAYOUT_PERM = 'cabinetkgmaplayout';
const VIEW_MODE_PERM = 'cabinetkgviewmode'; // 'cards' | 'table'
const FILTERS_PERM = 'cabinetkgfilters';
const MAP_WIDTH_PERM = 'cabinetkgmapwidth';

const MAP_WIDTH_MIN = 30;
const MAP_WIDTH_MAX = 70;
const MAP_WIDTH_DEFAULT = 50;

const PAGE_SIZE = 6;
// Наскільки заздалегідь (у px) до появи сентинела в зоні видимості починати довантаження
const INFINITE_SCROLL_THRESHOLD_PX = 600;
// Нижче цієї ширини "карта збоку" схлопується CSS-медіа-запитами в, по суті,
// той самий вигляд, що й "карта зверху" — тож і сам перемикач тут зайвий
const SIDE_LAYOUT_MIN_WIDTH_QUERY = '(max-width: 991.98px)';

// Helper: convert number string like "378", "110", "СЗШІ№5" -> numeric if possible
const getNumericNumber = (numberStr) => {
  if (!numberStr) return Infinity;
  const parsed = parseInt(numberStr, 10);
  return isNaN(parsed) ? Infinity : parsed;
};

// Sorting by number (numeric first, then string) and then by name (Ukrainian alphabet)
const sortByNumberAndName = (a, b) => {
  const numA = getNumericNumber(a.number);
  const numB = getNumericNumber(b.number);

  if (numA !== Infinity && numB !== Infinity) {
    if (numA !== numB) return numA - numB;
  } else if (numA !== Infinity) return -1;
  else if (numB !== Infinity) return 1;
  else {
    // both non-numeric, compare as strings
    const strA = String(a.number || '');
    const strB = String(b.number || '');
    if (strA !== strB) return strA.localeCompare(strB, 'uk');
  }

  // then by name
  const nameA = (a.name || '').toLowerCase();
  const nameB = (b.name || '').toLowerCase();
  return nameA.localeCompare(nameB, 'uk');
};

// Sort by name (Ukrainian alphabet)
const sortByName = (a, b) => {
  const nameA = (a.name || '').toLowerCase();
  const nameB = (b.name || '').toLowerCase();
  return nameA.localeCompare(nameB, 'uk');
};

// Detect if search query is numeric-only
const isNumericSearch = (searchStr) => /^\d+$/.test(searchStr.trim());

// Safely read & parse JSON from localStorage
const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

const clampMapWidth = (val) => {
  if (isNaN(val)) return MAP_WIDTH_DEFAULT;
  return Math.min(MAP_WIDTH_MAX, Math.max(MAP_WIDTH_MIN, val));
};

const KindergartensPage = ({ 
  showFilters = false, 
  isListView = false, 
  isChecked = false, 
  listChecked = [], 
  onCheck, 
  uncheckAll, 
  isFixed=false,
  intlAgreementOnly=false
}) => {

  const sel_pm_info = localStorage.getItem(SELECTED_KG_PERM);
  const sel_layout_info = localStorage.getItem(MAP_LAYOUT_PERM);
  const sel_view_mode_info = localStorage.getItem(VIEW_MODE_PERM);
  const sel_map_width_info = localStorage.getItem(MAP_WIDTH_PERM);

  const [filtersExpanded, setFiltersExpanded] = useState(showFilters);
  const [kindergartensData, setData] = useState([]);
  const [period, setPeriod] = useState("all");
  const [filters, setFilters] = useState(() => readJSON(FILTERS_PERM, getFilterObj()));
  const [viewcount, setViewCount] = useState(0);
  const [search, setSearch] = useState({ search: "", near: "", searchBy: "number", addressDistr: "", addressStr: "", addressBuilding: "", addressCorpus: ""});
  const [viewtables, setShowTables] = useState(
    //isChecked ? isListView : sel_view_mode_info ? sel_view_mode_info === 'table' : isListView
    isListView
  );
  const [selectedKg, setSelectedKg] = useState( sel_pm_info && JSON.parse(sel_pm_info) || []);
  const [mapLayout, setMapLayout] = useState(() => {
    // Якщо користувач уже колись явно обирав режим — поважаємо це значення
    if (sel_layout_info === 'side' || sel_layout_info === 'top') return sel_layout_info;
    // Немає збереженого вибору: за замовчуванням на десктопі — карта збоку,
    // на вузьких екранах (мобільні) — зверху (перемикача там і так немає)
    const isMobile = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia(SIDE_LAYOUT_MIN_WIDTH_QUERY).matches;
    return isMobile ? 'top' : 'side';
  }); // 'top' | 'side'
  const [mapWidthPercent, setMapWidthPercent] = useState(
    clampMapWidth(parseFloat(sel_map_width_info)) || MAP_WIDTH_DEFAULT
  );
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia(SIDE_LAYOUT_MIN_WIDTH_QUERY).matches
  );

  const layoutRef = useRef(null);
  const isDraggingRef = useRef(false);
  const sentinelRef = useRef(null);
  const tableScrollRef = useRef(null);
  const resultsAnchorRef = useRef(null);
  // Тримаємо актуальний "чи є ще що довантажувати" в ref, щоб не перестворювати
  // scroll/resize-обробники на кожен рендер/зміну viewcount
  const hasMoreRef = useRef(false);
  // Попередні значення пошуку — щоб скролити лише на РЕАЛЬНУ зміну тексту, а не
  // на "перший рендер" (булевий прапорець тут ненадійний: у React StrictMode
  // ефект монтування навмисно викликається двічі, і прапорець встигає збитись
  // ще до того, як користувач хоч раз щось ввів — звідси й зайвий скрол при
  // простому завантаженні/оновленні сторінки)
  const prevSearchRef = useRef({ search: search.search, near: search.near });
  // DOM-вузли карток/рядків таблиці за _id садочка — щоб скролити до потрібного
  // елемента при кліку на мітку карти, незалежно від того, картки зараз показані
  // чи таблиця
  const cardRefs = useRef({});
  const tableRowRefs = useRef({});
  const highlightTimeoutRef = useRef(null);

  const [mapSelectedId, setMapSelectedId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);

  const onCheck_ = (data) => {
    onCheck(data);
  }

  // ===== Клік по мітці на карті: скролимо список до відповідної картки/рядка
  // і на пару секунд підсвічуємо її, щоб було одразу видно, який саме садок
  // обрали на карті =====
  const handleMarkerClick = useCallback((kgId) => {
    const node = cardRefs.current[kgId] || tableRowRefs.current[kgId];
    if (!node) return;

    node.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setMapSelectedId(kgId);
    highlightTimeoutRef.current = setTimeout(() => setMapSelectedId(null), 2200);
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const getData = async () => {
    const result = await getKindergartensList({period, near_address: search.near, search: search.search, search_by: search.searchBy, filters, internationalAgreement: intlAgreementOnly});
    setViewCount(listChecked.length > PAGE_SIZE ? listChecked.length : PAGE_SIZE);
    setData(result);
    if(listChecked.length > 0) getSelectedKgInfo();
  }

  const addList = () => {
    setViewCount(v => v + PAGE_SIZE);
  }

  const getSelectedKgInfo = async () => {
    const selectedArchId = selectedKg.map(k=>k._id);
    const needInfoKgId = listChecked.filter(i=>!selectedArchId.includes(i));
    if(needInfoKgId.length < 1) return;
    const result = await getKindergartensList({filters: { 'id_kg': needInfoKgId }});
    const resArr = [...selectedKg, ...result]; 
    setSelectedKg(resArr);
  }

  useEffect(() => { getData() }, [period, filters, search, intlAgreementOnly]);
  useEffect(() => { localStorage.setItem(SELECTED_KG_PERM, JSON.stringify(listChecked)) }, [listChecked]);
  useEffect(() => { localStorage.setItem(MAP_LAYOUT_PERM, mapLayout) }, [mapLayout]);
  //useEffect(() => { localStorage.setItem(VIEW_MODE_PERM, viewtables ? 'table' : 'cards') }, [viewtables]);
  useEffect(() => { localStorage.setItem(FILTERS_PERM, JSON.stringify(filters)) }, [filters]);
  useEffect(() => { localStorage.setItem(MAP_WIDTH_PERM, String(mapWidthPercent)) }, [mapWidthPercent]);

  // ===== На вузьких екранах "карта збоку" не має сенсу: CSS і так схлопує її у
  // такий самий стек, як і "карта зверху" (див. @media 991.98px нижче), тож сам
  // перемикач лише плутає. Стежимо за viewport і, якщо він вузький, а обраний
  // режим — 'side', примусово повертаємо 'top'. Якщо вікно розширили назад —
  // режим самі НЕ повертаємо, це вже свідомий вибір користувача.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(SIDE_LAYOUT_MIN_WIDTH_QUERY);

    const handleChange = (e) => setIsNarrowViewport(e.matches);
    setIsNarrowViewport(mql.matches);

    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else mql.addListener(handleChange); // старі Safari

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else mql.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    if (isNarrowViewport && mapLayout === 'side') setMapLayout('top');
  }, [isNarrowViewport, mapLayout]);

  // ===== Мобільний UX: після того як користувач ввів пошуковий запит (search.search
  // оновлюється лише ПІСЛЯ дебаунсу в SearchFilterSection, тобто вже після завершення
  // вводу), автоматично прокручуємо до початку списку результатів. На мобільному екрані
  // список зазвичай ховається нижче форми пошуку й карти, і його не видно без ручного
  // гортання — це знімає той зайвий крок. На десктопі не чіпаємо: там результати й так
  // видно одразу поруч із пошуком.
  useEffect(() => {
    const prev = prevSearchRef.current;
    const changed = prev.search !== search.search || prev.near !== search.near;
    prevSearchRef.current = { search: search.search, near: search.near };
    if (!changed) return;

    const isMobile = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767.98px)').matches;
    if (!isMobile || !resultsAnchorRef.current) return;
    resultsAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [search.search, search.near]);

  const combUniqKg = (arrKg1, arrKg2) => {
    const combinedArray = [...arrKg1];
    const idsArr = combinedArray.map(g=>g._id);
    arrKg2.forEach(t => {
      if(!idsArr.includes(t._id)) combinedArray.push(t);
    });
    return combinedArray;
  }

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Apply sorting based on search query
  const sortedKindergartens = useMemo(() => {
    if (!kindergartensData.length) return kindergartensData;

    const searchQuery = search.search || '';
    if (!searchQuery) {
      // Default: sort by number, then name
      return [...kindergartensData].sort(sortByNumberAndName);
    } else if (isNumericSearch(searchQuery)) {
      // If searching by numbers -> sort by number (numeric)
      return [...kindergartensData].sort((a, b) => {
        const numA = getNumericNumber(a.number);
        const numB = getNumericNumber(b.number);
        if (numA !== Infinity && numB !== Infinity) return numA - numB;
        if (numA !== Infinity) return -1;
        if (numB !== Infinity) return 1;
        return sortByNumberAndName(a, b);
      });
    } else {
      // If searching by letters -> sort by name
      return [...kindergartensData].sort(sortByName);
    }
  }, [kindergartensData, search.search]);

  const combKgList = listChecked.length > 0 
    ? combUniqKg(sortedKindergartens, selectedKg.filter(sv=>listChecked.includes(sv._id))) 
    : sortedKindergartens;

  // Ensure selected items always on top for display (table already does this, but keep for cards)
  const sortedCombinedList = useMemo(() => {
    if (!isChecked || listChecked.length === 0) return combKgList;
    return [...combKgList].sort((a,b) => {
      const aChecked = listChecked.includes(a._id);
      const bChecked = listChecked.includes(b._id);
      if (aChecked !== bChecked) return aChecked ? -1 : 1;
      return 0;
    });
  }, [combKgList, listChecked, isChecked]);

  const combKgSlice = sortedCombinedList.slice(0, viewcount);

  // Оновлюємо на кожен рендер, щоб callback обсервера завжди бачив свіже значення
  hasMoreRef.current = sortedCombinedList.length > viewcount;

  // ===== Infinite scroll: довантажуємо наступну "сторінку" при наближенні до сентинела =====
  // Навмисно НЕ IntersectionObserver: сентинел лежить у flex-контейнері поруч зі
  // "position: sticky" картою (layout=side), і в такій комбінації IntersectionObserver
  // в частині браузерів/рушіїв рахує перетин непередбачувано. Пряма перевірка
  // getBoundingClientRect() по scroll/resize вікна не залежить від CSS-контексту
  // сентинела і однаково стабільно працює і в layout=top, і в layout=side —
  // зрештою в обох випадках скролиться саме window, а не внутрішній контейнер.
  // У табличному режимі сама таблиця тепер має власний внутрішній скрол
  // (max-height: 80vh, sticky thead) — там сентинел лежить УСЕРЕДИНІ цього
  // контейнера, і window може взагалі не скролитись. rect.top лишається
  // коректним (getBoundingClientRect завжди відносно viewport, незалежно від
  // того, який саме елемент скролиться), тож достатньо додатково слухати
  // 'scroll' ще й на самому контейнері таблиці.
  useEffect(() => {
    let ticking = false;

    const checkSentinel = (thresholdPx) => {
      ticking = false;
      if (!hasMoreRef.current || !sentinelRef.current) return;
      const rect = sentinelRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top <= viewportHeight + thresholdPx) {
        addList();
      }
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      // При реальному скролі/ресайзі підвантажуємо трохи заздалегідь — для плавності
      requestAnimationFrame(() => checkSentinel(INFINITE_SCROLL_THRESHOLD_PX));
    };

    // При монтуванні/зміні даних довантажуємо лише якщо сентинел вже реально
    // видно на екрані (без запасу вперед) — інакше початкова порція одразу ж
    // "з'їдає" наступну сторінку, ще до того як користувач хоч раз прогорнув.
    checkSentinel(0);

    const tableScrollNode = viewtables ? tableScrollRef.current : null;

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    if (tableScrollNode) tableScrollNode.addEventListener('scroll', onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (tableScrollNode) tableScrollNode.removeEventListener('scroll', onScrollOrResize);
    };
  }, [kindergartensData.length, viewcount, mapLayout, viewtables]);


  // Adaptive column count: fewer columns when map takes up side space
  const cardColClass = mapLayout === 'side' ? 'col-lg-6' : 'col-lg-4';

  // ===== Drag-to-resize map width (only in 'side' layout) =====
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.classList.add('kg-resizing');
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !layoutRef.current) return;
      const rect = layoutRef.current.getBoundingClientRect();
      if (rect.width <= 0) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const percent = ((clientX - rect.left) / rect.width) * 100;
      setMapWidthPercent(clampMapWidth(percent));
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.classList.remove('kg-resizing');
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  const isSideLayout = mapLayout === 'side';

  return (
    <div className={isListView ? 'list-view' : ''}>
      <div className={`kg-page-layout layout-${mapLayout}`} ref={layoutRef}>
        {!isListView && (
          <div
            className="kg-map-wrap"
            style={isSideLayout ? { width: `${mapWidthPercent}%` } : undefined}
          >
            <MapPlaceholder kglist={combKgSlice} layout={mapLayout} onMarkerClick={handleMarkerClick} hoveredKgId={hoveredCardId}/>
          </div>
        )}

        {!isListView && isSideLayout && (
          <div
            className="kg-resize-divider"
            onMouseDown={handleDividerMouseDown}
            onTouchStart={handleDividerMouseDown}
            title="Перетягніть, щоб змінити ширину карти"
          >
            <div className="kg-resize-divider-handle"></div>
          </div>
        )}

        <div
          className="kg-content-wrap"
          style={isSideLayout ? { width: `${100 - mapWidthPercent}%` } : undefined}
        >
          <div className="container my-5">
            {!isListView && (
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
                <h1 className="mb-0">Усі дитячі садочки Києва</h1>
                {!isNarrowViewport && (
                  <div className="map-layout-switch">
                    <button
                      type="button"
                      className={mapLayout === 'top' ? 'active' : ''}
                      onClick={() => setMapLayout('top')}
                    >
                      Карта зверху
                    </button>
                    <button
                      type="button"
                      className={mapLayout === 'side' ? 'active' : ''}
                      onClick={() => setMapLayout('side')}
                    >
                      Карта збоку
                    </button>
                  </div>
                )}
              </div>
            )}

            <SearchFilterSection 
              isExpanded={filtersExpanded} 
              onToggleFilters={toggleFilters}
              searchFilter={setSearch}
              search={search}
              setFilters={setFilters}
              filters={filters}
            />

            {/* Якір для автоскролу на мобільному після пошуку/фільтрації —
                тримаємо перед лічильником результатів, щоб після скролу
                користувач одразу бачив і "Відображається X з Y", і перший ряд карток */}
            <div ref={resultsAnchorRef} />

            <ResultsSection 
              totalCount={kindergartensData.length} 
              displayedCount={kindergartensData.length > viewcount ?  viewcount: kindergartensData.length} 
              isListView={viewtables}
              onlyList={isListView}
              setListView={setShowTables}
              Checked={isChecked ? listChecked.length : 0}     
            />

            {viewtables ? 
              <KindergartensTable 
                tableData={combKgSlice}
                isChecked={isChecked}
                listChecked={listChecked}
                onCheck={onCheck_}
                uncheckAll={uncheckAll}
                isFixed={isFixed}
                registerRowRef={(id, el) => {
                  if (el) tableRowRefs.current[id] = el;
                  else delete tableRowRefs.current[id];
                }}
                mapSelectedId={mapSelectedId}
                onRowHoverChange={(id, leavingId) => setHoveredCardId((cur) => {
                  if (id !== null) return id;
                  return cur === leavingId ? null : cur;
                })}
                scrollContainerRef={tableScrollRef}
                sentinelRef={sentinelRef}
                hasMore={kindergartensData.length > viewcount}
              />
              : 
              <div className="row g-4">
                {combKgSlice.map((kindergarten, idx) => (
                  <div
                    key={kindergarten.id}
                    className={`col-md-6 ${cardColClass}`}
                    ref={(el) => {
                      if (el) cardRefs.current[kindergarten._id] = el;
                      else delete cardRefs.current[kindergarten._id];
                    }}
                    onMouseEnter={() => setHoveredCardId(kindergarten._id)}
                    onMouseLeave={() => setHoveredCardId((cur) => cur === kindergarten._id ? null : cur)}
                  >
                    <KindergartenCard 
                      kindergarten={kindergarten} 
                      highlighted={kindergarten.highlighted}
                      mapSelected={mapSelectedId === kindergarten._id}
                      index={idx}
                    />
                  </div>
                ))}
              </div>
            }

            {/* У режимі таблиці сентинел рендериться самою таблицею (усередині
                її внутрішнього скролу) — тут його дублювати не треба */}
            {!viewtables && (
              <div ref={sentinelRef} className="kg-infinite-scroll-sentinel">
                {kindergartensData.length > viewcount && (
                  <div className="kg-infinite-scroll-loader">
                    <span className="kg-infinite-scroll-spinner" aria-hidden="true" />
                    <span>Завантаження...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KindergartensPage;